import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const KEYS = ["hero", "about", "sermons", "donate", "contact", "banner", "announcements", "marquee", "choice", "featured", "schedule", "give"];
export const DEFAULT_SETTINGS = {
  stream_url: "",
  donate_enabled: true,
  sections: Object.fromEntries(KEYS.map((k) => [k, true])),
  banner_url: "/images/banner.jpg",
  schedule: [],
  text: { en: {}, mh: {} },
};

const SiteContext = createContext(null);

// Visual Editor modes: "edit" (inside the portal editor) or "preview" (new tab). Kept for the browser tab session.
const detectMode = () => {
  const q = new URLSearchParams(window.location.search).get("ve");
  if (q === "off") sessionStorage.removeItem("aik-ve");
  else if (q === "edit" || q === "preview") sessionStorage.setItem("aik-ve", q);
  const mode = sessionStorage.getItem("aik-ve") || "";
  const framed = window.parent !== window;
  if ((mode === "edit" && !framed) || (mode === "preview" && framed)) return "";
  return mode;
};

export const SiteProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [live, setLive] = useState(null);
  const [draft, setDraft] = useState(null);
  const [veMode] = useState(detectMode);
  const [announcements, setAnnouncements] = useState([]);
  const [pages, setPages] = useState([]);

  const loadDraft = useCallback(() => {
    if (!veMode) return;
    api.get("/design/draft").then((r) => setDraft(r.data?.data || null)).catch(() => setDraft(null));
  }, [veMode]);

  const refresh = useCallback(() => {
    api.get("/settings").then((r) => setLive({ ...DEFAULT_SETTINGS, ...(r.data || {}) })).catch(() => setLive({ ...DEFAULT_SETTINGS }));
    api.get("/announcements").then((r) => setAnnouncements(Array.isArray(r.data) ? r.data : [])).catch(() => setAnnouncements([]));
    api.get("/pages").then((r) => setPages(Array.isArray(r.data) ? r.data : [])).catch(() => setPages([]));
    loadDraft();
  }, [loadDraft]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (live) setSettings(draft ? { ...live, ...draft, sections: { ...live.sections, ...(draft.sections || {}) } } : live);
  }, [live, draft]);

  useEffect(() => {
    if (!veMode) return undefined;
    const onMsg = (e) => {
      if (e.origin === window.location.origin && e.data?.type === "ve-reload") loadDraft();
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [veMode, loadDraft]);

  const on = (k) => settings?.sections?.[k] !== false;
  return <SiteContext.Provider value={{ settings, ready: settings !== DEFAULT_SETTINGS, setSettings: setLive, announcements, pages, refresh, on, veMode, draft, setDraft, loadDraft }}>{children}</SiteContext.Provider>;
};

export const useSite = () => useContext(SiteContext);
